import { FC } from 'react'
import { Virtuoso } from 'react-virtuoso'

import { CircularProgress, Stack, Typography } from '@mui/material'
import { AutoCompleteOption } from '@/components/inputs/LicenseCompetitionsAutocomplete/LicenseCompetitionsAutocomplete'

import { CompetitionCheckboxItem } from './CompetitionCheckboxItem'
import { COMPETITIONS_LIST_HEIGHT, COMPETITIONS_LIST_MAX_HEIGHT } from './constants'

type AvailableCompetitionsListProps = {
  unselectedCompetitionsList: AutoCompleteOption[]
  selectedCompetitionIds: string[]
  onCompetitionToggle: (competition: AutoCompleteOption) => void
  debouncedSearchQuery: string
  isLoading: boolean
  hasMore: boolean
  isLoadingMore: boolean
  isAutoLoading: boolean
  currentPage: number
  loadMore: (page: number) => void
  selectedCompetitionsListLength: number
}

export const AvailableCompetitionsList: FC<AvailableCompetitionsListProps> = ({
  unselectedCompetitionsList,
  selectedCompetitionIds,
  onCompetitionToggle,
  debouncedSearchQuery,
  isLoading,
  hasMore,
  isLoadingMore,
  isAutoLoading,
  currentPage,
  loadMore,
  selectedCompetitionsListLength
}) => {
  const isSearching = debouncedSearchQuery && debouncedSearchQuery.trim() !== ''

  if (isSearching && isLoading) {
    return (
      <Stack
        sx={{
          height: COMPETITIONS_LIST_HEIGHT,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Stack>
    )
  }

  if (!unselectedCompetitionsList.length) {
    return (
      <Stack sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No available competitions
        </Typography>
      </Stack>
    )
  }

  return (
    <Virtuoso
      data={unselectedCompetitionsList}
      style={{
        height:
          debouncedSearchQuery.trim().length || !selectedCompetitionsListLength
            ? COMPETITIONS_LIST_MAX_HEIGHT
            : COMPETITIONS_LIST_HEIGHT
      }}
      endReached={() => {
        if (hasMore && !isLoadingMore && !isAutoLoading) {
          loadMore(currentPage + 1)
        }
      }}
      itemContent={(index, competition) => {
        const isSelected = selectedCompetitionIds.includes(competition.value)

        return (
          <CompetitionCheckboxItem
            key={competition.value}
            competition={competition}
            isSelected={isSelected}
            onToggle={onCompetitionToggle}
          />
        )
      }}
    />
  )
}
